import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import type { EntityOnboarding } from '@onefootprint/request-types/dashboard';
import { Button, Tooltip } from '@onefootprint/ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import StatusBadge from '@/components/status-badge';
import useOnboardingKindText from '@/hooks/use-onboarding-kind-text';
import { hasVisibleStatus, onboardingHasPlaybook } from '../../../onboardings-shared/utils';
import useOnboardingTitle from '../../hooks/use-onboarding-title';
import useOpenTrace from '../../hooks/use-open-trace';
import OnboardingDetails from './components/onboarding-details';
type DecryptAction = {
  canDecrypt: boolean;
  isDecrypting: boolean;
  onDecrypt: () => void;
};
type OnboardingHeaderProps = {
  onboarding: EntityOnboarding;
  /** Omitted once nothing the onboarding collected is left encrypted, and 'pending' until that's known. */
  decrypt?: DecryptAction | 'pending';
};
const OnboardingHeader = t0 => {
  const $ = _c(55);
  const {
    onboarding,
    decrypt
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const onboardingKindT = useOnboardingKindText();
  const getTitle = useOnboardingTitle();
  const openTrace = useOpenTrace();
  const playbook = onboardingHasPlaybook(onboarding) ? onboarding.playbook : undefined;
  let t2;
  if ($[1] !== formatDateWithTime) {
    t2 = timestamp => formatDateWithTime(new Date(timestamp), {
      month: "short",
      year: "numeric"
    });
    $[1] = formatDateWithTime;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const formatTimestamp = t2;
  const titleRef = useRef(null);
  const isDecryptFocusedRef = useRef(false);
  const hasDecrypt = Boolean(decrypt) && decrypt !== "pending";
  let t3;
  let t4;
  if ($[3] !== hasDecrypt) {
    t3 = () => {
      if (!hasDecrypt && isDecryptFocusedRef.current) {
        isDecryptFocusedRef.current = false;
        titleRef.current?.focus();
      }
    };
    t4 = [hasDecrypt];
    $[3] = hasDecrypt;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== getTitle || $[7] !== onboarding) {
    t5 = getTitle(onboarding);
    $[6] = getTitle;
    $[7] = onboarding;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== playbook || $[10] !== t) {
    t6 = playbook && <>{" "}<span className="text-tertiary">{playbook.isHidden ? t(_temp) : t(_temp2, {
          version: playbook.version
        })}</span></>;
    $[9] = playbook;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <h3 ref={titleRef} tabIndex={-1} className="min-w-0 text-heading-4 text-primary">{t5}{t6}</h3>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== onboarding) {
    t8 = hasVisibleStatus(onboarding) && <StatusBadge status={onboarding.status} />;
    $[15] = onboarding;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== onboarding.kind || $[21] !== onboardingKindT || $[22] !== playbook) {
    t10 = playbook && `${onboardingKindT(onboarding.kind)} · `;
    $[20] = onboarding.kind;
    $[21] = onboardingKindT;
    $[22] = playbook;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== formatTimestamp || $[25] !== onboarding.timestamp) {
    t11 = formatTimestamp(onboarding.timestamp);
    $[24] = formatTimestamp;
    $[25] = onboarding.timestamp;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11) {
    t12 = <p className="text-body-2 text-tertiary">{t10}{t11}</p>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t12 || $[31] !== t9) {
    t13 = <div className="flex min-w-0 flex-col gap-1">{t9}{t12}</div>;
    $[30] = t12;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== decrypt || $[34] !== t) {
    t14 = decrypt === "pending" && <span className="invisible" aria-hidden={true}><Button size="compact">{t(_temp3)}</Button></span>;
    $[33] = decrypt;
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== decrypt || $[37] !== t) {
    t15 = decrypt && decrypt !== "pending" && <Tooltip disabled={decrypt.canDecrypt} content={t(_temp4)}><Button size="compact" disabled={!decrypt.canDecrypt} loading={decrypt.isDecrypting} onClick={decrypt.onDecrypt} onFocus={() => {
        isDecryptFocusedRef.current = true;
      }} onBlur={event => {
        if (event.relatedTarget) {
          isDecryptFocusedRef.current = false;
        }
      }}>{t(_temp5)}</Button></Tooltip>;
    $[36] = decrypt;
    $[37] = t;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== onboarding.hasGraphTrace || $[40] !== onboarding.id || $[41] !== openTrace || $[42] !== t) {
    t16 = onboarding.hasGraphTrace && <Button variant="secondary" size="compact" onClick={event_0 => openTrace(event_0, onboarding.id)}>{t(_temp6)}</Button>;
    $[39] = onboarding.hasGraphTrace;
    $[40] = onboarding.id;
    $[41] = openTrace;
    $[42] = t;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== formatTimestamp || $[45] !== onboarding) {
    t17 = <OnboardingDetails onboarding={onboarding} formatTimestamp={formatTimestamp} />;
    $[44] = formatTimestamp;
    $[45] = onboarding;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== t14 || $[48] !== t15 || $[49] !== t16 || $[50] !== t17) {
    t18 = <div className="flex min-w-0 flex-wrap items-center gap-2">{t14}{t15}{t16}{t17}</div>;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== t13 || $[53] !== t18) {
    t19 = <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-5 pt-5 pb-4">{t13}{t18}</header>;
    $[52] = t13;
    $[53] = t18;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  return t19;
};
export default OnboardingHeader;
function _temp($) {
  return $.header.unsaved;
}
function _temp2($_0) {
  return $_0.header.version;
}
function _temp3($_1) {
  return $_1.userData.decrypt.decryptAll;
}
function _temp4($_2) {
  return $_2.decryptPermissionError;
}
function _temp5($_3) {
  return $_3.userData.decrypt.decryptAll;
}
function _temp6($_4) {
  return $_4.header.openTrace;
}